import { logger } from '@pkg/shared/logger'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { Button, Card } from 'antd'
import { FaExclamationTriangle, FaRedo } from 'react-icons/fa'

interface DefaultErrorComponentProps {
  error: Error
  reset?: () => void
}

export function DefaultErrorComponent({ error, reset }: DefaultErrorComponentProps) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const isDev = process.env.NODE_ENV === 'development'

  const handleRetry = async () => {
    if (reset) {
      reset()
    }
    await queryClient.invalidateQueries()
    // Invalidate the router to trigger a refresh
    await router.invalidate()
    if (typeof location !== 'undefined') {
      location.reload()
    }
  }

  // Log error with cause chain
  const cause = (error as Error & { cause?: unknown }).cause
  logger.error({ err: error, cause }, 'Internal error')

  // Build cause chain for display
  const causeChain: Array<{ message: string; stack?: string }> = []
  let currentCause = cause
  while (currentCause) {
    if (currentCause instanceof Error) {
      causeChain.push({ message: currentCause.message, stack: currentCause.stack })
      currentCause = (currentCause as Error & { cause?: unknown }).cause
    } else if (typeof currentCause === 'string') {
      causeChain.push({ message: currentCause })
      break
    } else {
      causeChain.push({ message: JSON.stringify(currentCause, null, 2) })
      break
    }
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <div className="p-8 text-center">
        <div className="flex items-center justify-center text-red-600 mb-6">
          <FaExclamationTriangle className="mr-3 text-2xl" />
          <h2 className="text-xl font-semibold">{isDev ? 'Development Error' : 'Etwas ist schief gelaufen'}</h2>
        </div>
        {isDev ? (
          <div className="text-left mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-red-800 mb-2">Error Details:</h3>
              <p className="text-red-700 font-mono text-sm break-all">{error.message}</p>
            </div>

            {causeChain.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-orange-800 mb-2">Caused by:</h3>
                {causeChain.map((c, i) => (
                  <div key={c.message} className="mb-2 last:mb-0">
                    <p className="text-orange-700 font-mono text-sm break-all">
                      {i + 1}.{c.message}
                    </p>
                    {c.stack && (
                      <pre className="text-xs text-orange-600 overflow-auto whitespace-pre-wrap mt-1 ml-4">
                        {c.stack}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            )}

            {error.stack && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-2">Stack Trace:</h3>
                <pre className="text-xs text-gray-600 overflow-auto whitespace-pre-wrap">{error.stack}</pre>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-6">
            <p className="text-gray-600 text-lg">
              Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen Sie es erneut.
            </p>
          </div>
        )}

        <div className="flex justify-center gap-4">
          <Button type="primary" onClick={() => void handleRetry()} icon={<FaRedo />}>
            Erneut versuchen
          </Button>

          <Button onClick={() => void router.navigate({ to: '/' })}>Zur Startseite</Button>
        </div>
      </div>
    </Card>
  )
}
